import { useMemo, useState, useEffect } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { ROUTES, PERMISSIONS } from '@/constants';
import { useAuthContext } from '@/context/AuthContext';
import { filterNavItems } from '@/utils/navAccess';
import { cn } from '@/lib/utils';
import {
    APP_SIDEBAR_ASIDE_CLASS,
    APP_SIDEBAR_HEADER_ROW_CLASS,
    APP_SIDEBAR_TOGGLE_BUTTON_CLASS,
    appSidebarNavClassName,
    APP_SIDEBAR_SECTION_LABEL_CLASS,
    appSidebarNavLinkClassName,
} from '@/lib/sidebarNav';
import { LayoutDashboard, ListTodo, BookOpen, ChevronsUp, Network } from 'lucide-react';
import { TripleChevronCloseIcon, TripleChevronOpenIcon } from '@/components/layout/PortalSidebarToggle';
import { useIsMobile } from '@/hooks/useIsMobile';
import { ExecutiveAppHeader } from '@/components/layout/ExecutiveAppHeader';
import { DashboardHeaderSlotProvider, DashboardHeaderSlot } from '@/context/DashboardHeaderSlotContext';
import { ChatProvider } from '@/features/chat/context/ChatContext';
import { AiAssistantProvider } from '@/features/aiAssistant/context/AiAssistantContext';
import { AiAssistantNavItem } from '@/features/aiAssistant/components/AiAssistantNavItem';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectChatConversations, selectMessagesByConversation } from '@/features/chat/store/chatSelectors';
import { fetchConversations } from '@/features/chat/services/chatService';
import { setChatError, setConversations, setSidebarMeta } from '@/features/chat/store/chatSlice';
import { selectAuthUser } from '@/features/auth/store/authSelectors';
import { useChatSocket } from '@/features/chat/hooks/useChatSocket';

export function MemberLayout() {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const isMobile = useIsMobile();
    const { user } = useAuthContext();
    const dispatch = useAppDispatch();
    const account = useAppSelector(selectAuthUser);
    const conversations = useAppSelector(selectChatConversations);
    const messagesByConversation = useAppSelector(selectMessagesByConversation);
    const userId = user?.userId ? String(user.userId) : '';
    const { emitJoinConversation } = useChatSocket(userId);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (!userId) return;
            dispatch(setChatError(null));
            try {
                const result = await fetchConversations({
                    appRole: user?.role ?? null,
                    userId,
                    account,
                });
                if (cancelled) return;
                dispatch(setConversations(Array.isArray(result?.data) ? result.data : []));
                if (result?.meta) dispatch(setSidebarMeta(result.meta));
            } catch (e) {
                if (cancelled) return;
                dispatch(setChatError(e?.message ?? 'Failed to load conversations'));
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [dispatch, userId, user?.role, account]);

    useEffect(() => {
        if (!emitJoinConversation) return;
        (conversations || []).forEach((c) => {
            if (c?.id) emitJoinConversation(String(c.id));
        });
    }, [conversations, emitJoinConversation]);

    // Map unread task-scoped chat conversations into header notifications.
    const chatNotifications = useMemo(() => {
        return (conversations || [])
            .filter((c) => c && c.unreadCount > 0 && c.scopeType === 'task' && c.scopeId)
            .map((c) => {
                const convMessages = messagesByConversation?.[c.id] || [];
                const latest = convMessages[convMessages.length - 1];
                const preview =
                    typeof latest?.body === 'string' && latest.body.trim()
                        ? latest.body.trim()
                        : typeof latest?.text === 'string' && latest.text.trim()
                            ? latest.text.trim()
                            : null;

                return {
                    id: `chat-${c.id}`,
                    title: c.name || 'Task conversation',
                    subtitle: preview || 'New message in task chat',
                    meta: 'New message',
                    kind: 'chat',
                    conversationId: c.id,
                    taskId: c.scopeId,
                };
            });
    }, [conversations, messagesByConversation]);

    const tabs = useMemo(
        () => [
            {
                label: 'Overview',
                path: ROUTES.MEMBER,
                end: true,
                icon: LayoutDashboard,
                requiredPermissions: [PERMISSIONS.VIEW_OWN_TASK],
            },
            {
                label: 'My Tasks',
                path: ROUTES.MEMBER_TASKS,
                icon: ListTodo,
                requiredPermissions: [PERMISSIONS.VIEW_OWN_TASK],
            },
            {
                label: 'Escalations',
                path: ROUTES.MEMBER_ESCALATION_HISTORY,
                icon: ChevronsUp,
                requiredPermissions: [PERMISSIONS.VIEW_ESCALATION],
            },
            {
                label: 'Structure',
                path: ROUTES.MEMBER_STRUCTURE,
                icon: Network,
                // No permission or role gate: every account may see the structure.
                // Where the tree is rooted per role is decided by the server.
            },
            {
                label: 'User Manual',
                path: ROUTES.MEMBER_HELP,
                icon: BookOpen,
                requiredPermissions: [PERMISSIONS.VIEW_OWN_TASK],
            },
        ],
        [],
    );

    const visibleTabs = useMemo(() => filterNavItems(user, tabs), [user, tabs]);

    return (
        <DashboardHeaderSlotProvider>
            <ChatProvider>
                <AiAssistantProvider>
                <div className="flex h-screen h-dvh flex-col gap-3 overflow-hidden bg-background p-3">
                    <ExecutiveAppHeader
                        title="Member Dashboard"
                        subtitle="Assigned tasks & readiness"
                        center={<DashboardHeaderSlot />}
                        unreadNotifications={chatNotifications}
                        showChatButton={false}
                        onOpenSidebar={() => setSidebarOpen(true)}
                    />

                    <div className="relative flex min-h-0 flex-1 overflow-hidden">
                        {isMobile && sidebarOpen ? (
                            <div
                                className="fixed inset-0 z-30 bg-black/40 md:hidden"
                                onClick={() => setSidebarOpen(false)}
                                aria-hidden
                            />
                        ) : null}
                        <aside
                            className={cn(
                                APP_SIDEBAR_ASIDE_CLASS,
                                isMobile || sidebarOpen ? 'w-64' : 'w-14',
                                isMobile && !sidebarOpen ? '-translate-x-full' : 'translate-x-0',
                            )}
                        >
                            <div className={APP_SIDEBAR_HEADER_ROW_CLASS}>
                                {sidebarOpen ? (
                                    <div className="flex min-w-[16rem] w-full justify-end px-2 py-2">
                                        <button
                                            type="button"
                                            onClick={() => setSidebarOpen(false)}
                                            className={APP_SIDEBAR_TOGGLE_BUTTON_CLASS}
                                            aria-label="Collapse sidebar to icons only"
                                            title="Show icons only"
                                        >
                                            <TripleChevronCloseIcon />
                                        </button>
                                    </div>
                                ) : (
                                    <div className="flex w-full justify-center py-2">
                                        <button
                                            type="button"
                                            onClick={() => setSidebarOpen(true)}
                                            className={APP_SIDEBAR_TOGGLE_BUTTON_CLASS}
                                            aria-label="Expand sidebar"
                                            title="Expand sidebar"
                                        >
                                            <TripleChevronOpenIcon />
                                        </button>
                                    </div>
                                )}
                            </div>
                            <nav className={appSidebarNavClassName(sidebarOpen)}>
                                {sidebarOpen ? (
                                    <p className={APP_SIDEBAR_SECTION_LABEL_CLASS}>
                                        Navigation
                                    </p>
                                ) : null}
                                {visibleTabs.map(({ requiredPermissions: _rp, requiredRole: _rr, ...tab }) => (
                                    <NavLink
                                        key={tab.path}
                                        to={tab.path}
                                        end={tab.end}
                                        title={tab.label}
                                        onClick={() => isMobile && setSidebarOpen(false)}
                                        className={({ isActive }) =>
                                            appSidebarNavLinkClassName({ isActive, expanded: sidebarOpen })
                                        }
                                    >
                                        <tab.icon className="h-5 w-5 flex-shrink-0" aria-hidden />
                                        <span className={cn('leading-tight', !sidebarOpen && 'sr-only')}>{tab.label}</span>
                                    </NavLink>
                                ))}
                                <AiAssistantNavItem
                                    expanded={sidebarOpen}
                                    onActivate={() => isMobile && setSidebarOpen(false)}
                                />
                            </nav>
                        </aside>

                        <main className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden p-0.5 md:p-3">
                            <Outlet />
                        </main>
                    </div>
                </div>
                </AiAssistantProvider>
            </ChatProvider>
        </DashboardHeaderSlotProvider>
    );
}

export default MemberLayout;
